import type { App } from "vue";
import type { AxiosResponse } from "axios";
import axios from "axios";
import VueAxios from "vue-axios";
import JwtService from "@/core/services/JwtService";
import { useSelectedCompany } from "@/core/helpers/useSelectedCompany";

/**
 * @description service to call HTTP request via Axios
 */
class ApiService {
  /**
   * @description property to share vue instance
   */
  public static vueInstance: App;

  private static withCompanyIdConfig(config: any = {}) {
    const { selectedCompanyIdReadonly } = useSelectedCompany();
    const company_id = selectedCompanyIdReadonly.value;
    if (!company_id) return config;

    const nextConfig = { ...(config || {}) };
    nextConfig.params = { ...(nextConfig.params || {}) };
    if (!nextConfig.params.company_id) {
      nextConfig.params.company_id = company_id;
    }
    return nextConfig;
  }

  private static withCompanyIdPayload(payload?: any) {
    const { selectedCompanyIdReadonly } = useSelectedCompany();
    const company_id = selectedCompanyIdReadonly.value;
    if (!company_id) return payload;

    if (payload instanceof FormData) {
      if (!payload.has("company_id")) {
        payload.append("company_id", company_id);
      }
      return payload;
    }

    if (payload && typeof payload === "object" && !Array.isArray(payload)) {
      if (!payload.company_id) {
        payload.company_id = company_id;
      }
      return payload;
    }

    if (payload === undefined) {
      return { company_id };
    }

    return payload;
  }

  /**
   * @description initialize vue axios
   */
  public static init(app: App<Element>) {
    ApiService.vueInstance = app;
    ApiService.vueInstance.use(VueAxios, axios);
    ApiService.vueInstance.axios.defaults.baseURL =
      import.meta.env.VITE_APP_API_URL;
    // Axios 1.x uses URLSearchParams by default (encodes space as +).
    // Use encodeURIComponent so spaces appear as %20 in query strings.
    ApiService.vueInstance.axios.defaults.paramsSerializer = {
      encode: encodeURIComponent,
    };
  }

  /**
   * @description set the default HTTP request headers
   */
  public static setHeader(): void {
    ApiService.vueInstance.axios.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${JwtService.getToken()}`;
    ApiService.vueInstance.axios.defaults.headers.common["Accept"] =
      "application/json";
  }

  /**
   * @description send the GET HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static query(resource: string, params: any): Promise<AxiosResponse> {
    ApiService.setHeader();
    return ApiService.vueInstance.axios.get(resource, ApiService.withCompanyIdConfig(params));
  }

  /**
   * @description send the GET HTTP request
   * @param resource: string
   * @param slug: string
   * @returns Promise<AxiosResponse>
   */
  // public static get(
  //   resource: string,
  //   slug = "" as string
  // ): Promise<AxiosResponse> {
  //   this.setHeader();
  //   if (slug === "") {
  //     return ApiService.vueInstance.axios.get(`${resource}`);
  //   }
  //   return ApiService.vueInstance.axios.get(`${resource}/${slug}`);
  // }

  public static get(
    resource: string,
    config = {},
  ): Promise<AxiosResponse> {
    this.setHeader();
    return ApiService.vueInstance.axios.get(`${resource}`, ApiService.withCompanyIdConfig(config));
  }


  /**
   * @description set the POST HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */

  public static post(resource: string, data?: any, config?: any): Promise<AxiosResponse> {
    ApiService.setHeader();
    return ApiService.vueInstance.axios.post(
      resource,
      ApiService.withCompanyIdPayload(data),
      ApiService.withCompanyIdConfig(config)
    );
  }

  /**
   * @description send the UPDATE HTTP request
   * @param resource: string
   * @param slug: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static update(
    resource: string,
    slug: string,
    params: any
  ): Promise<AxiosResponse> {
    ApiService.setHeader();
    return ApiService.vueInstance.axios.put(`${resource}/${slug}`, ApiService.withCompanyIdPayload(params));
  }

  /**
   * @description Send the PUT HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static put(resource: string, params: any): Promise<AxiosResponse> {
    ApiService.setHeader();
    return ApiService.vueInstance.axios.put(`${resource}`, ApiService.withCompanyIdPayload(params));
  }

  /**
   * @description Send the DELETE HTTP request
   * @param resource: string
   * @returns Promise<AxiosResponse>
   */
  public static delete(resource: string): Promise<AxiosResponse> {
    ApiService.setHeader();
    return ApiService.vueInstance.axios.delete(resource, ApiService.withCompanyIdConfig());
  }

  /**
   * @description Send the DELETE HTTP request with a request body (useful for bulk deletions)
   * @param resource: string
   * @param data: any - request payload to send with DELETE
   * @returns Promise<AxiosResponse>
   */
  public static deleteWithBody(resource: string, data: any): Promise<AxiosResponse> {
    ApiService.setHeader();
    const config = ApiService.withCompanyIdConfig({ data: ApiService.withCompanyIdPayload(data) });
    return ApiService.vueInstance.axios.delete(resource, config);
  }

  /**
   * @description Send the PATCH HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static patch(resource: string, params: any): Promise<AxiosResponse> {
    ApiService.setHeader();
    return ApiService.vueInstance.axios.patch(`${resource}`, ApiService.withCompanyIdPayload(params));
  }
}

export default ApiService;
